import { z } from 'zod';
import { MeasurementTypeEnum, MeasurementContextSchema } from './pregnancy.schema';

/**
 * @file measurement.schema.ts
 * @description Medições clínicas ao longo do tempo — peso, pressão, glicemia,
 * biometria fetal.
 *
 * ## Por que saiu de dentro da gestação
 *
 * Eram um array em `pregnancy.measurements`. Duas consequências:
 *
 * **Uma tentante não tinha onde registrar peso.** Não existe documento de
 * gestação para ela, e sem peso não há IMC — que é indicador do painel dela. É
 * o mesmo problema do `clinicalBaseline`, resolvido na Fase 2, agora para série
 * temporal.
 *
 * **Array dentro de documento não escala.** Teto de 1 MB por documento e sem
 * paginação: uma gestação com pesagem semanal e biometria cabe, um
 * acompanhamento de anos não.
 *
 * ## A ligação
 *
 * `userId` é obrigatório — a medição é **da mulher**. `pregnancyId` é opcional e
 * marca a qual gestação ela pertence, quando pertence. Uma tentante grava sem
 * ele; uma gestante grava com.
 *
 * Isso também resolve continuidade: o peso registrado antes de engravidar
 * continua sendo dela depois, em vez de ficar preso a um episódio.
 */
export const MeasurementRecordSchema = z.object({
  id: z.string(),

  /** Campo `id` da usuária — não o id do documento dela. */
  userId: z.string(),

  /** Presente quando a medição pertence a uma gestação. */
  pregnancyId: z.string().nullable().optional(),
  /**
   * Marca da sessão em que o registro aconteceu (A052, fase 2). Opcional
   * enquanto o dado antigo é backfillado; a leitura passa a filtrar por ele
   * na fase 3. Cada CRM vê só o que aconteceu na própria marca.
   */
  brandId: z.string().nullable().optional(),

  date: z.coerce.date(),
  type: MeasurementTypeEnum,
  value: z.number(),
  context: MeasurementContextSchema.optional(),
  recordedBy: z.string().optional(),

  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type MeasurementRecord = z.infer<typeof MeasurementRecordSchema>;
